import { useQueryGetAllBots } from "@apis:bot/bot";
import { useQueryGetAllOrganizations } from "@apis:organization/organization";
import { SuperDataGrid } from "@components:SuperDataGrid";
import { usePaginationSortDataGrid } from "@hooks:usePaginationSortDataGrid";
import { useTableFilter } from "@hooks:useTableFilter";
import { Box, useTheme } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { OrganizationDeleteModal } from "@pages:settings/Tenent/Organizations/OrganizationDeleteModal";
import { OrganizationModal } from "@pages:settings/Tenent/Organizations/OrganizationModal";
import { useOrganizationsData } from "@stores:settings/organizations";
import { formatDateTime } from "@utils:datetime";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdDelete, MdModeEdit } from "react-icons/md";

export const OrganizationsTable = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isOpenModalToUpdate, setIsOpenModalToUpdate] = useState(false);
  const [isOpenModalToDelete, setIsOpenModalToDelete] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    string | null
  >(null);
  const { searchKeyword, state } = useOrganizationsData();
  const { paginationProps, sortProps } = usePaginationSortDataGrid({
    pageSize: 30,
    defaultSortModel: {
      field: "name",
      sort: "asc",
    },
  });

  const { data, isLoading } = useQueryGetAllOrganizations();
  const { data: botsData } = useQueryGetAllBots();

  const botCounts = useMemo(() => {
    if (!botsData) return {};
    return botsData.reduce(
      (acc: Record<string, { enabled: number; total: number }>, bot) => {
        const orgId = bot.organization_id;
        if (!acc[orgId]) {
          acc[orgId] = { enabled: 0, total: 0 };
        }

        acc[orgId].total += 1;
        if (bot.enabled) {
          acc[orgId].enabled += 1;
        }

        return acc;
      },
      {},
    );
  }, [botsData]);

  const cleanedData = useTableFilter({
    filters: [
      {
        field: "enabled",
        operator: "equals",
        value: state === "active",
      },
      {
        field: "name",
        operator: "contains",
        value: searchKeyword,
      },
    ],
    data,
  });

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "id",
        headerName: t("common.id"),
        flex: 2,
        cellClassName: "font-mono",
      },
      {
        field: "name",
        headerName: t("common.name"),
        flex: 1,
        cellClassName: "font-mono",
      },
      {
        field: "bots",
        headerName: t("tenant.organizations.botsColumn"),
        flex: 1,
        valueParser: (_value, row) => {
          const counts = botCounts[row.id];
          return counts?.enabled || 0;
        },
        valueGetter: (_value, row) => {
          const counts = botCounts[row.id];
          if (!counts || counts.total === 0) {
            return t("tenant.organizations.noBots");
          }

          const active = counts.enabled || 0;
          const inactive = counts.total - active;

          if (active === counts.total) {
            return t("tenant.organizations.botsActive", { value: active });
          }
          if (active === 0) {
            return t("tenant.organizations.botsInactive", { value: inactive });
          }

          return t("tenant.organizations.botsActiveInactive", {
            active,
            inactive,
          });
        },
        cellClassName: "font-mono",
      },
      {
        field: "createdAt",
        headerName: t("common.createdAt"),
        flex: 1,
        valueParser: (_value, row) => row.createdAt,
        valueGetter: (_value, row) => formatDateTime(row.createdAt),
        cellClassName: "font-mono",
      },
      {
        field: "updatedAt",
        headerName: t("common.updatedAt"),
        flex: 1,
        valueParser: (_value, row) => row.updatedAt,
        valueGetter: (_value, row) => formatDateTime(row.updatedAt),
        cellClassName: "font-mono",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        flex: 1,
        align: "right",
        getActions: (params: any) => {
          const editButton = (
            <GridActionsCellItem
              key="edit"
              icon={<MdModeEdit size={20} />}
              label={t("common.edit")}
              className="textPrimary"
              color="primary"
              onClick={() => {
                setIsOpenModalToUpdate(true);
                setSelectedOrganizationId(params.row.id);
              }}
            />
          );

          const deleteButton = (
            <GridActionsCellItem
              key="delete"
              icon={<MdDelete size={20} />}
              label={t("common.delete")}
              style={{ color: theme.palette.error.main }}
              onClick={async () => {
                setIsOpenModalToDelete(true);
                setSelectedOrganizationId(params.row.id);
              }}
            />
          );

          const actions = [editButton];
          if (params?.row?.enabled) {
            actions.push(deleteButton);
          }

          return actions;
        },
      },
    ],
    [botCounts, t, theme.palette.error.main],
  );

  return (
    <Box
      sx={{
        height: "100%",
      }}
    >
      <SuperDataGrid
        columns={columns}
        rows={cleanedData}
        loading={isLoading}
        {...paginationProps}
        {...sortProps}
        for="organizations"
        noRowsOverlayText={t("tenant.organizations.noneFound")}
        sx={{
          "& .MuiDataGrid-row--borderBottom": {
            backgroundColor: "white !important",
          },
        }}
      />
      {isOpenModalToUpdate && (
        <OrganizationModal
          isOpen={isOpenModalToUpdate}
          onClose={() => setIsOpenModalToUpdate(false)}
          organizationId={selectedOrganizationId!}
        />
      )}
      {isOpenModalToDelete && (
        <OrganizationDeleteModal
          isOpen={isOpenModalToDelete}
          onClose={() => setIsOpenModalToDelete(false)}
          organizationId={selectedOrganizationId!}
        />
      )}
    </Box>
  );
};
